import { ModuleAccess } from '../../types/auth.model';
import { Link, useSearchParams } from 'react-router-dom';
import React, { useState } from 'react';
import { useFetchData } from '../../hooks';
import { PageableResponse } from '../../types';
import { PurchaseOrderStatusEnums, Resource } from '../../lib/enums';
import {
  convertDate,
  dateFormat,
  dateTimeFormat,
  getEnumValue,
} from '../../lib/utils';
import { Table, TableColumnsType } from 'antd';
import InfoModal from '../../components/ui/modal/InfoModal';
import StatusTag from '../../components/ui/StatusTag';
import { PurchaseOrder } from '../../types/PurchaseOrder';
import dayjs from 'dayjs';

const PurchaseOrderTable = ({
  api,
  moduleName,
}: {
  api: string;
  moduleName: string;
  userModuleAccess: ModuleAccess | undefined;
  userRole?: string;
}) => {
  const [searchParams] = useSearchParams();
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);

  const startDate =
    searchParams.get('start') === undefined ||
    searchParams.get('start') === null
      ? dayjs().subtract(6, 'month').format(dateFormat)
      : searchParams.get('start');

  const endDate =
    searchParams.get('end') === undefined || searchParams.get('end') === null
      ? dayjs().add(2, 'month').format(dateFormat)
      : searchParams.get('end');

  const { data, isLoading } = useFetchData<PageableResponse<PurchaseOrder>>(
    api,
    {
      resource: searchParams.get('resource') || Resource.FULL,
      search: searchParams.get('search') || '',
      from: startDate,
      to: endDate,
      status: getEnumValue(
        PurchaseOrderStatusEnums,
        searchParams.get('status') ?? '',
      ),
      length: parseInt(searchParams.get('length') || '999999'),
      // optional supplier-accreditation filter; absent = all POs
      ...(searchParams.get('accredited')
        ? { accredited: searchParams.get('accredited') }
        : {}),
    },
  );

  const columns: TableColumnsType<PurchaseOrder> = [
    {
      title: 'Code',
      dataIndex: 'code',
      render: (_text: string, record: PurchaseOrder) => {
        return (
          <Link to={`/purchase-order/${record.id}?action=view`}>
            <span className={'text-blue-400 font-semibold'}>
              {record.code || record.id}
            </span>
          </Link>
        );
      },
    },
    { title: 'Order Date', dataIndex: 'orderDate' },
    { title: 'Source', dataIndex: 'source' },
    { title: 'Required Date', dataIndex: 'requiredDate' },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      render: (_text: string, record: PurchaseOrder) =>
        `${record?.vendor?.description === undefined ? '' : record.vendor?.description}`,
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      render: (_text: string, record: PurchaseOrder) =>
        `${record?.destination?.description === undefined ? '' : record.destination?.description}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_text: any, record: PurchaseOrder) => (
        <StatusTag status={record.status} />
      ),
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (_text: string, record: PurchaseOrder) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.createdBy}</span>
            {record.timeCreated != null && (
              <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Update By/Date',
      dataIndex: 'updatedBy',
      render: (_text: string, record: PurchaseOrder) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.updatedBy}</span>
            {record.timeUpdated != null && (
              <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
  ];

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  return (
    <>
      <div className={'flex flex-col h-[calc(79vh)] overflow-auto'}>
        <Table
          rowKey={(record) => record.id}
          dataSource={data?.data}
          loading={isLoading}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          columns={columns}
        />
      </div>
      <InfoModal
        visible={isInfoModalOpen}
        message={`${moduleName} successfully deleted`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};
export default PurchaseOrderTable;
